'use client'

import { ReactNode, useEffect, useRef } from 'react'
import { Lock } from 'lucide-react'
import Link from '@/modules/shared/components/Link'
import { useAccessControl, AccessContext } from '../hooks/useAccessControl'
import posthog from 'posthog-js'
import { useAuth } from '@/modules/auth/contexts/AuthContext'

const PAYWALL_BLUR_CLASS = 'blur-[1px]' // Dễ dàng thay đổi độ mờ tại đây, ví dụ: 'blur-sm', 'blur-md', 'blur-[4px]'

interface AccessControlWrapperProps {
    children: ReactNode
    context: AccessContext
}

export default function AccessControlWrapper({
    children,
    context,
}: AccessControlWrapperProps) {
    const { isAllowed, action, reason } = useAccessControl(context)
    const { user } = useAuth()
    const trackedViewRef = useRef(false)

    const isShowingPaywall = action === 'blur-paywall' || !isAllowed

    const paywallRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!isShowingPaywall) return

        let observer: IntersectionObserver | null = null
        const currentElement = paywallRef.current

        if (currentElement && !trackedViewRef.current) {
            observer = new IntersectionObserver(
                (entries) => {
                    const [entry] = entries
                    if (entry.isIntersecting && !trackedViewRef.current) {
                        trackedViewRef.current = true;
                        posthog.capture('paywall_viewed', {
                            hs_code: context.hsCode,
                            page_url: window.location.href,
                            page_path: window.location.pathname,
                        })

                        if (observer) {
                            observer.disconnect()
                        }
                    }
                },
                { threshold: 0.7 } // Kích hoạt khi paywall hiển thị ít nhất 10%
            )

            observer.observe(currentElement)
        }

        return () => {
            if (observer) {
                observer.disconnect()
            }
        }
    }, [isShowingPaywall, reason, context, user?.email])

    const handleActionClick = (actionType: 'login' | 'register' | 'go_home') => {
        posthog.capture(`paywall_${actionType}_clicked`, {
            hs_code: context.hsCode,
            page_url: window.location.href,
            page_path: window.location.pathname,
        })
    }

    if (isShowingPaywall) {
        return (
            <div ref={paywallRef} className="relative overflow-hidden min-h-[500px] max-h-[800px]">
                {/* Blurred Content */}
                <div className={`pointer-events-none select-none opacity-60 transition-all duration-500 ${PAYWALL_BLUR_CLASS}`}>
                    {children}
                </div>

                {/* Paywall Overlay */}
                <div className="absolute inset-0 z-10 flex flex-col items-center pt-24 bg-linear-to-b from-transparent via-white/80 to-white pb-10">
                    <div className="bg-white p-8 rounded-2xl shadow-xl max-w-[480px] w-full mx-auto text-center border border-gray-100 flex flex-col items-center">
                        <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-6">
                            <Lock size={32} />
                        </div>
                        <h2 className="text-[22px] font-bold text-gray-900 mb-4">
                            Tiếp tục truy cập dữ liệu mã HS
                        </h2>
                        <p className="text-gray-600 mb-8 whitespace-pre-line leading-relaxed text-[15px]">
                            {reason || 'Vui lòng đăng nhập để xem nội dung này.'}
                        </p>
                        <div className="flex flex-col w-full gap-3">
                            <Link
                                href="https://caselaw.vn/auth/login"
                                onClick={() => handleActionClick('login')}
                                className="w-full py-3 px-4 bg-[#00479C] text-white text-[14px] font-[600] rounded-[5px] hover:bg-[#003870] transition-colors"
                            >
                                Đăng nhập
                            </Link>
                            <Link
                                href="https://caselaw.vn/auth/register"
                                onClick={() => handleActionClick('register')}
                                className="w-full py-3 px-4 bg-white text-[#00479C] text-[14px] font-[600] rounded-[5px] border-2 border-[#00479C] hover:bg-gray-50 transition-colors"
                            >
                                Đăng ký miễn phí
                            </Link>
                        </div>
                        <Link
                            href="https://caselaw.vn/"
                            onClick={() => handleActionClick('go_home')}
                            className="mt-6 text-[13px] text-gray-500 hover:text-gray-700 underline underline-offset-2"
                        >
                            Về trang chủ
                        </Link>
                    </div>
                </div>
            </div>
        )
    }

    // Not blocked, just render normally
    return <>{children}</>
}
